import cx from 'classnames';
import React, { forwardRef } from 'react';
import { BaseLink } from './base-link';
import styles from './link.module.scss';

export function shouldInvokeLinkHandler(
  event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  target?: string
) {
  return (
    event.button === 0 &&
    (!target || target === '_self') &&
    !(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey)
  );
}

export type LinkColors = 'blue' | 'black' | 'white';
export type LinkComponentProps = {
  /**
   * color of the link
   * @default 'blue'
   */
  color?: LinkColors;

  /**
   * disabled state
   * @default false
   */
  disabled?: boolean;

  /**
   * enable/disable underline on hover
   * @default false
   */
  noUderline?: boolean;
};

export type LinkProps = LinkComponentProps &
  React.AnchorHTMLAttributes<HTMLAnchorElement> &
  React.RefAttributes<HTMLAnchorElement>;

export type RoutedLinkProps = LinkProps & {
  /**
   * allow modified link actions for links
   * @default true
   */
  allowModifiedLinkActions?: boolean;
};

export const Link: React.FC<LinkProps> = forwardRef<HTMLAnchorElement, LinkProps>(
  (
    { children, color = 'blue', className, disabled, noUderline = false, ...restProps }: LinkProps,
    ref
  ) => {
    const containerClassNames = cx(className, styles.link, {
      [styles.blue]: color === 'blue',
      [styles.black]: color === 'black',
      [styles.white]: color === 'white',
      [styles.disabled]: disabled,
      [styles.noUderline]: noUderline,
    });
    return (
      <BaseLink ref={ref} className={containerClassNames} {...restProps}>
        {children}
      </BaseLink>
    );
  }
);
Link.displayName = 'Link';

export const RoutedLink: React.FC<RoutedLinkProps> = forwardRef<HTMLAnchorElement, RoutedLinkProps>(
  ({ children, allowModifiedLinkActions = true, onClick, ...restProps }: RoutedLinkProps, ref) => {
    const clickHandler = React.useMemo(() => {
      if (!allowModifiedLinkActions) {
        return (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
          e.preventDefault();
          onClick?.(e);
        };
      }
      return (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        if (shouldInvokeLinkHandler(e)) {
          e.preventDefault();
          onClick?.(e);
        }
      };
    }, [allowModifiedLinkActions, onClick]);
    return (
      <Link ref={ref} {...restProps} onClick={clickHandler}>
        {children}
      </Link>
    );
  }
);
RoutedLink.displayName = 'RoutedLink';
